import {
  Box,
  Button,
  Flex,
  IconButton,
  ListIcon,
  ListItem,
  Text,
  Tooltip,
  UnorderedList,
  useTheme,
  useToast,
  Image,
  Heading,
} from '@chakra-ui/react';
import React, { useEffect, useMemo } from 'react';
import Present from '../assets/Present.svg';
import NotPresent from '../assets/notPresent.svg';

import {
  AddSquareIcon,
  ConfirmButton,
  DataGrid,
  Loader,
  NestedGridTable,
  SubtractSquareIcon,
  useToastHook,
} from '@devd-client/devd/components';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '@devd-client/api';
import { getToken, getOrgId } from '@devd-client/devd/auth';
import { createColumnHelper } from '@tanstack/react-table';
import { QUERY_KEYS, useFetchTeams } from '../apis';
import { useQueryClient } from '@tanstack/react-query';
import { MdOutlineEdit, MdPersonAddAlt } from 'react-icons/md';
import { TEAM_DETAILS_QUERY_KEYS } from '@devd-client/devd/team-details';

interface TeamViewProps {
  team: string;
  setTeamLength: any;
  appState?: any;
  teamLength?: number;
  plan?: string;
}

const TeamView: React.FC<TeamViewProps> = ({
  team,
  setTeamLength,
  appState,
  teamLength,
  plan,
}) => {
  const queryClient = useQueryClient();
  const { colors } = useTheme();
  const navigate = useNavigate();
  const [loading, setLoading] = React.useState(false);
  const [newToast] = useToastHook();

  const { data: teamsData, isFetching: teamsDataLoading } = useFetchTeams(team);

  const rows: any[] = useMemo(() => teamsData?.dto || [], [teamsData?.dto]);

  useEffect(() => {
    setTeamLength(rows.length);
  }, [rows, setTeamLength]);

  const columnHelper = createColumnHelper<any>();

  const columns = useMemo(
    () => [
      columnHelper.display({
        id: 'expand',
        size: 10,
        cell: (info) => {
          const row = info.row;
          const depth = row.depth;
          const isExpanded = row.getIsExpanded();
          const canExpand = row.getCanExpand();
          const allRows = info.table.getRowModel().rows;
          const parentRow = allRows.find((r) =>
            r.subRows?.some((sr) => sr.id === row.id)
          );
          const isLast =
            parentRow?.subRows[parentRow.subRows.length - 1]?.id === row.id;

          return (
            <Box position="absolute" top={3} left={0} right={0} bottom={0}>
              {/* Full-height line for non-last child rows (connects to next sibling) */}
              {depth > 0 && !isLast && (
                <Box
                  position="absolute"
                  left="50%"
                  transform="translateX(-50%)"
                  top="-16px"
                  height="calc(100% + 16px)"
                  width="1px"
                  bg={colors.text.gray[100]}
                />
              )}
              {/* Half-height line for last LEAF child rows (no expand button) */}
              {depth > 0 && isLast && !canExpand && (
                <>
                  <Box
                    position="absolute"
                    left="50%"
                    transform="translateX(-50%)"
                    top="-90px"
                    height="calc(50% + 62px)"
                    width="1px"
                    bg={colors.text.gray[100]}
                  />
                  <Box
                    position="absolute"
                    left="50%"
                    top={'21px'}
                    height="1px"
                    width="10px"
                    bg={colors.text.gray[100]}
                  />
                </>
              )}
              {/* Short line from top to button for last expandable child rows */}
              {depth > 0 && isLast && canExpand && (
                <Box
                  position="absolute"
                  left="50%"
                  transform="translateX(-50%)"
                  top="-12px"
                  height="22px"
                  width="1px"
                  bg={colors.text.gray[100]}
                />
              )}
              {/* Line from button downward when expanded */}
              {canExpand && isExpanded && (
                <Box
                  position="absolute"
                  left="50%"
                  transform="translateX(-50%)"
                  top="20px"
                  height="calc(100% - 18px)"
                  width="1px"
                  bg={colors.text.gray[100]}
                />
              )}
              {/* Button pinned to the top, white bg punches through the line */}
              {canExpand && (
                <Box
                  position="absolute"
                  top="4px"
                  left="50%"
                  transform="translateX(-50%)"
                  zIndex={1}
                  bg="white"
                >
                  <Button
                    variant="unstyled"
                    minW="auto"
                    w={4}
                    h={4}
                    p={0}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    onClick={row.getToggleExpandedHandler()}
                  >
                    {isExpanded ? (
                      <SubtractSquareIcon height="16" width="16" />
                    ) : (
                      <AddSquareIcon height="16" width="16" />
                    )}
                  </Button>
                </Box>
              )}
            </Box>
          );
        },
        header: '',
        meta: { noPadding: true },
      }),
      columnHelper.accessor('name', {
        cell: (info) => info.getValue(),
        header: 'Team Name',
        enableSorting: false,
      }),

      columnHelper.accessor('description', {
        cell: (info) => {
          if (!info.getValue()) {
            return <Box color="orange.600">No Description Added</Box>;
          } else {
            return info.getValue();
          }
        },
        header: 'Description',
        size: 360,
        enableSorting: false,
      }),

      columnHelper.accessor('managers', {
        cell: (info) => {
          let count = 0;
          info.row.original.subTeams.forEach((item: any) => {
            count += item.managers.length;
          });
          return (
            <Flex flexWrap="wrap">
              {info?.getValue()?.length > 0 ? (
                <Box>
                  {info?.getValue()?.map((item: string, idx: number) => (
                    <Text mr={2} key={`manager-${idx}`}>
                      {item}
                      {info.getValue().length - 1 !== idx && ','}
                    </Text>
                  ))}
                  {info.row.original.subTeams.length > 0 ? (
                    <Text>+{count} in subteams</Text>
                  ) : (
                    ''
                  )}
                </Box>
              ) : (
                <Box>
                  <Text color={'gray.400'} fontFamily={'heading'}>
                    No Manager Assigned
                  </Text>
                  {info.row.original.subTeams.length > 0 ? (
                    <Text>+{count} in subteams</Text>
                  ) : (
                    ''
                  )}
                </Box>
              )}
            </Flex>
          );
        },
        header: 'Managers',
        enableSorting: false,
      }),
      columnHelper.accessor('memberCount', {
        cell: (info) => {
          const rowData = info.row.original as any;
          const managersCount = rowData?.managers
            ? rowData?.managers?.length
            : 0;
          const membersCount = rowData?.memberCount ? rowData?.memberCount : 0;
          const subTeamsCount = rowData?.subTeams
            ? rowData?.subTeams?.length
            : 0;

          return (
            <Flex
              flexDirection="column"
              gap="2"
              width="100px"
              alignItems="flex-start"
            >
              <Text>{managersCount} Managers</Text>
              <Text>{membersCount} Members</Text>
              <Text>{subTeamsCount} Subteams</Text>
            </Flex>
          );
        },
        header: 'Members',
        size: 50,
        meta: {
          isNumeric: true,
        },
        enableSorting: false,
      }),
      columnHelper.accessor('setupStates', {
        cell: (info) => (
          <UnorderedList listStyleType={'none'}>
            <ListItem display={'flex'} gap={1}>
              {info.getValue().Communication ? (
                <Image src={Present}></Image>
              ) : (
                <Image src={NotPresent}></Image>
              )}
              Communication
            </ListItem>
            <ListItem display={'flex'} gap={1}>
              {info.getValue().Dora ? (
                <Image src={Present}></Image>
              ) : (
                <Image src={NotPresent}></Image>
              )}
              DORA
            </ListItem>
            <ListItem display={'flex'} gap={1}>
              {info.getValue().Issue ? (
                <Image src={Present}></Image>
              ) : (
                <Image src={NotPresent}></Image>
              )}
              Issue
            </ListItem>
            <ListItem display={'flex'} gap={1}>
              {info.getValue().Git ? (
                <Image src={Present}></Image>
              ) : (
                <Image src={NotPresent}></Image>
              )}
              Git
            </ListItem>
          </UnorderedList>
        ),
        header: 'Config Status',
        enableSorting: false,
      }),

      columnHelper.accessor('actions', {
        cell: (info) => (
          <Flex>
            <Tooltip label="Edit" hasArrow>
              <IconButton
                aria-label="edit"
                bg="transparent"
                size="sm"
                color="text.secondary"
                icon={<MdOutlineEdit size={16} />}
                position={'static'}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  navigate(`/settings/teams/${info.row.original?.name}`);
                }}
              />
            </Tooltip>

            <ConfirmButton
              buttonText="Delete"
              showIcon
              bodyText={`Do you really want to delete this team - ${info.row.original?.name}?`}
              headerText="Are you sure?"
              onSuccessAction={() => {
                if (info.row.original.subTeams.length >= 1) {
                  newToast({
                    message:
                      'Cannot delete top level team if sub teams available.',
                    status: 'error',
                  });
                } else {
                  handleDelete(info.row.original?.name);
                }
              }}
            />

            <Tooltip label="Add Member" hasArrow>
              <IconButton
                aria-label="edit"
                size="sm"
                bg="transparent"
                color="primary"
                icon={<MdPersonAddAlt size={16} />}
                position={'static'}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  navigate(`/settings/teams/${info.row.original?.name}`, {
                    state: { tab: 1 },
                  });
                }}
              />
            </Tooltip>
          </Flex>
        ),
        header: 'Actions',
        enableSorting: false,
      }),
    ],
    [teamsData?.dto]
  );

  const handleDelete = (name: string) => {
    setLoading(true);
    apiClient(`/v1/team/${getOrgId()}/${name}`, {
      method: 'DELETE',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        authorization: `bearer ${getToken()}`,
      },
    })
      .then((res: any) => {
        Promise.all([
          queryClient.invalidateQueries([QUERY_KEYS.teams]),
          queryClient.invalidateQueries([TEAM_DETAILS_QUERY_KEYS.teamList]),
          queryClient.invalidateQueries(['teamMenu']),
        ]);

        newToast({
          message: `"${name}" Team has been deleted successfully.`,
          status: 'success',
        });
      })
      .catch((err: any) => {
        newToast({
          message:
            err?.message ?? `Some error occurred while deleting ${name}.`,
          status: 'error',
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <>
      <NestedGridTable
        renderSubComponent={() => <></>}
        getSubRows={(rows: any) => rows.subTeams}
        getRowCanExpand={(row: any) =>
          row?.original?.subTeams && row?.original?.subTeams?.length > 0
        }
        showLoader={teamsDataLoading}
        columns={columns}
        data={rows}
        maxH="40rem"
        sticky="firstCol"
        disableBuiltinLines={true}
        noDataView={
          <Flex
            marginInline={'auto'}
            mt={16}
            w={'fit-content'}
            direction={'column'}
            justifyContent={'center'}
            alignItems={'center'}
          >
            {/*<Image src={NoTeam}></Image>*/}
            <Heading as="h2" fontWeight={'semibold'}>
              Uh no! No teams found
            </Heading>
            <Button
              fontSize={'md'}
              mt={6}
              variant={'filled'}
              colorScheme="twitter"
              borderRadius={4}
              onClick={() => {
                if (
                  appState?.user?.dto?.teamAllowed &&
                  (plan === 'Free' || plan === 'Trial') &&
                  teamLength! >= appState?.user?.dto?.teamAllowed
                ) {
                  newToast({
                    message:
                      'Upgrade your plan to add more teams, or contact Devdynamics support',
                    status: 'error',
                  });
                } else {
                  window.analytics.track('team_created', {
                    status: 'init',
                  });
                  navigate('/settings/teams/add-team', {
                    state: { newTeam: true },
                  });
                }
              }}
            >
              Create Team
            </Button>
          </Flex>
        }
      />
      {(loading || teamsDataLoading) && <Loader />}
    </>
  );
};

export default TeamView;
